import {
  ChangeDetectorRef,
  Component,
  HostBinding,
  inject,
  OnDestroy,
  OnInit,
  Type,
  ChangeDetectionStrategy,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  BubbleDataPoint,
  Chart,
  ChartConfiguration,
  ChartDataset,
  LegendItem,
  Point,
  registerables,
  TooltipItem,
} from 'chart.js';
import { catchError, combineLatest, map, of, shareReplay, switchMap, tap } from 'rxjs';
import { PanelService } from 'src/app/dashboards/panels/services/panel/panel.service';
import { VisibleDirective, waitUntilVisible } from 'src/app/panels/directives/visible/visible.directive';
import { NumberPipe } from 'src/app/panels/pipes/number/number.pipe';
import { CardComponent } from 'src/app/shared/components/card/card.component';
import { ErrorMessageComponent } from 'src/app/shared/components/error-message/error-message.component';
import { MessageComponent } from 'src/app/shared/components/message/message.component';
import { ColorService } from 'src/app/shared/services/color/color.service';
import { Color } from 'src/app/shared/services/color/colors';
import { v4 as uuidv4 } from 'uuid';
import { DisplayUnit } from '../../../models/display-unit';
import { IPieChartData } from '../../../models/pie';
import { PanelComponent } from '../../panel.component';

export const PIE_PANEL_IMPORTS: Type<unknown>[] = [
  CardComponent,
  VisibleDirective,
  ErrorMessageComponent,
  MessageComponent,
  NumberPipe,
];

@Component({
  selector: 'app-pie-panel',
  changeDetection: ChangeDetectionStrategy.Eager,
  template: '',
})
export abstract class PiePanelComponent extends PanelComponent implements OnInit, OnDestroy {
  @HostBinding('class') public class = 'col-12 col-lg-6';

  public chart!: Chart;
  public canvasId: string = uuidv4();
  public legendHtml: SafeHtml = [];
  public dataset: (number | [number, number] | Point | BubbleDataPoint | null)[] = [];
  public visibleData: boolean[] = [];
  public data?: IPieChartData | undefined;
  public cardParams!: {
    key: string;
    value: string;
  }[];
  public empty: boolean = false;
  public readonly colors?: Color[] = undefined;

  protected total: number | null = null;

  private panelService = inject(PanelService);
  private colorValues!: string[];
  private readonly numberPipe = new NumberPipe();
  private initialDataset: ChartDataset | null = null;

  private sanitizer = inject(DomSanitizer);
  private cdr = inject(ChangeDetectorRef);
  private colorService = inject(ColorService);

  public abstract override readonly title: string;
  public abstract readonly unit: DisplayUnit;
  public abstract override readonly icon?: string;

  ngOnInit(): void {
    this.colorValues = this.colorService.getColors(this.colors);
    this.data$ = combineLatest([this.dashboardParams$, this.visible$]).pipe(
      waitUntilVisible(this),
      switchMap((params) => {
        return this.panelService
          .getPie(
            this.getType(),
            this.entityIds,
            params.timeRange.from,
            params.timeRange.till,
            params.timeRange.compareFrom,
            params.timeRange.compareTill,
          )
          .pipe(
            catchError(() => {
              this.error = 'Geen data';
              return of(null);
            }),
          );
      }),
      map(
        (x) =>
          ({
            unit: this.unit,
            values: x?.map((v) => ({
              label: v.key,
              value: v.value,
            })),
          }) as IPieChartData,
      ),
      tap((data) => {
        this.loading = false;
        if (!data) return;
        this.data = data;
        this.initialDataset = null;
        this.cdr.detectChanges();
        this.createChart();
        this.updateChart();
      }),
      shareReplay(1),
    );
    this.data$.subscribe();
  }

  ngOnDestroy(): void {
    // Clean up chart to prevent memory leaks
    if (this.chart) {
      this.chart.destroy();
      this.chart = null!;
    }
  }

  public onLegendClick(event: Event): void {
    const index = (event.target as HTMLElement).getAttribute('data-index');
    if (index !== null) {
      this.toggleDatasetVisibility(parseInt(index));
    }
  }

  private updateChart(): void {
    const chartData = this.createChartData(this.data!);
    if (this.empty) {
      this.loading = false;
      return;
    }

    this.chart.data = chartData.data;
    this.chart.update();
    if (this.chart.legend?.legendItems) {
      this.chart.legend.legendItems.forEach((value: LegendItem, i: number) => {
        this.visibleData[i] = true;
        this.dataset[i] = this.chart.data.datasets[0].data[i];
      });
    }
    this.updateVisibleTotal();
    this.generateCustomLegend(this.chart);
    this.loading = false;
  }

  private createChart(): void {
    if (this.data?.values === undefined) {
      this.empty = true;
      return;
    }

    this.total = this.data?.values.reduce((a, b) => a + b.value, 0) ?? null;
    this.empty = !this.data || this.total === 0;

    // Destroy existing chart if it exists to prevent canvas reuse errors
    if (this.chart) {
      this.chart.destroy();
      this.chart = null!;
    }

    Chart.register(...registerables);
    const chart = new Chart(this.canvasId, this.createChartData(this.data!));
    this.chart = chart;
  }

  private createChartData(input: IPieChartData): ChartConfiguration {
    const type = 'doughnut';
    const values = input?.values ?? [];
    const data = {
      labels: values.map((x) => x.label) ?? [],
      datasets: [
        {
          unit: input?.unit ?? '',
          data: values.map((x) => x.value) ?? [],
          hoverOffset: 4,
          backgroundColor: this.colorValues,
        },
      ],
    };
    return {
      type,
      data,
      options: {
        layout: {
          padding: 4,
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            callbacks: {
              label: this.getLabel.bind(this),
            },
          },
        },
      },
    };
  }

  private generateCustomLegend(chart: Chart): void {
    const labels = chart.legend!.legendItems!;
    const dataset: ChartDataset = this.initialDataset ?? chart.data.datasets[0];
    this.initialDataset = structuredClone(dataset);

    const text = [];
    text.push('<ul>');

    for (let i = 0; i < labels.length; i++) {
      const label = labels[i];
      const labelClass = this.visibleData[i] ? '' : 'strike-through';
      text.push(
        `<div class="legend-value" tabindex="0">
          <div class="legend-icon" style="background: ${label.fillStyle}" data-index="${i}"></div>
          <span class="${labelClass} legend-text" data-index="${i}">${label.text}: <b data-index="${i}">
          ${this.numberPipe.transform(dataset.data[i] as number | null)} ${(dataset as unknown as IPieChartData).unit}</b></span>
        </div>`,
      );
    }

    text.push('</ul>');

    this.legendHtml = this.sanitizer.bypassSecurityTrustHtml(text.join(''));
    this.cdr.detectChanges();
  }

  private toggleDatasetVisibility(index: number): void {
    this.visibleData[index] = !this.visibleData[index];
    if (this.visibleData[index]) {
      this.chart.data.datasets[0].data[index] = this.dataset[index];
    } else {
      this.chart.data.datasets[0].data[index] = 0;
    }
    this.updateVisibleTotal();
    this.generateCustomLegend(this.chart);
    this.chart.update();
  }

  private updateVisibleTotal(): void {
    this.total = this.chart.data.datasets[0].data.reduce<number>((sum, value) => {
      return typeof value === 'number' ? sum + value : sum;
    }, 0);
  }

  private getLabel(context: TooltipItem<'doughnut'> & { dataset: { unit: string } }): string {
    return `${this.numberPipe.transform(context.parsed)} ${context.dataset.unit}`;
  }
}
